// Responsibile for loading and presenting the currently visible tutoring session. In order
// for the loading to work correctly a TutoringSession must be added to the stack in one of
// 3 states: fully hydrated, with just a `domain` or with `domain` and an `id`.

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { find, isNull } from 'lodash';
import moment from 'moment';
import { ErrorBoundary } from 'react-error-boundary';
import { v4 as uuidv4 } from 'uuid';

import LoadingIndicator from '@magoosh/layouts/components/loading_indicator';
import Dropdown from '@magoosh/lib/dropdown';
import { FontAwesomeIcon } from '@magoosh/lib/icons';
import RatingCollection from '@magoosh/organisms/rating_collection';
import usePathname from '@magoosh/utils/use_pathname';
import { bugsnagNotify } from 'utilities/bugsnag';

import * as api from '@test_prep/app/ai_tutor/api';
import { useAITutorContext } from '@test_prep/app/ai_tutor/context';
import { Domain, Interaction, Skill } from '@test_prep/app/ai_tutor/types';

import ErrorMessage from '../error_message';
import GradientPill from '../gradient_pill';
import { CrossIcon, iconForSkill } from '../icons';
import InteractionsList from '../interactions_list';
import UserInput from '../user_input';

import colors from '@magoosh/lib/styles/colors.module.scss';
import style from './style.module.scss';

const TutoringPanel: React.FC<{}> = (props) => {
  const {
    activity,
    clearActivity,
    clearStack,
    currentContext,
    handleNewInteractions,
    hideTutor,
    popFromStack,
    pushToStack,
    replaceTopOfStack,
    scrollTop,
    setScrollTop,
    setActivity,
    tutoringSessionsStack
  } = useAITutorContext();
  const [askForRating, setAskForRating] = useState(null);
  const [error, setError] = useState(null);
  const [uiStateApplied, setUIStateApplied] = useState(false);

  const handleInteractionsScroll = useRef(() => {
    setScrollTop(document.getElementById('tutoring-panel-interactions').scrollTop);
  });

  const tutoringSession = tutoringSessionsStack.at(-1);

  useEffect(() => {
    if (!tutoringSession) return;

    if (tutoringSessionFullyHydrated()) {
      if (isNull(askForRating)) {
        setAskForRating(!tutoringSession.rating && tutoringSession.ratingStatus !== 'declined');
      }

      handlePathChange(currentContext().path);
    } else {
      hydrateTutoringSession();
    }
  }, [tutoringSession, currentContext().path]);

  useEffect(() => {
    const interactionsElement = document.getElementById('tutoring-panel-interactions')
    if (interactionsElement) {
      document.getElementById('tutoring-panel-interactions').scrollTop = scrollTop;
    }

    setUIStateApplied(true);
  },[currentContext().path]);

  useLayoutEffect(() => {
    document.getElementById('tutoring-panel-interactions')?.addEventListener(
      'scroll',
      handleInteractionsScroll.current
    );

    return () => {
      document.getElementById('tutoring-panel-interactions')?.removeEventListener(
        'scroll',
        handleInteractionsScroll.current
      );
    }
  }, []);

  const fetchTutoringSession = (id: number) => {
    return api.fetchAITutoringSession(id).catch((error) => {
      setError(true);
      logError(error);
    });
  };

  const handlePathChange = (pathname) => {
    if (!tutoringSession) return;
    if (!tutoringSessionFullyHydrated()) return;

    const mostRecentContextChange = tutoringSession.interactions.filter(
      (interaction) => interaction.type === 'context_change'
    ).at(-1);
    if (!mostRecentContextChange) return;

    if (mostRecentContextChange.content.path !== pathname) {
      api.createAITutoringSessionContextChangeInteraction({
        content: currentContext(),
        tutoringSessionId: tutoringSession.id
      }).then((interactions) => {
        handleNewInteractions(interactions);
      });
    }
  };

  const hydrateTutoringSession = () => {
    clearActivity();
    setError(null);

    if (tutoringSession.id) {
      fetchTutoringSession(tutoringSession.id).then((session) => {
        replaceTopOfStack(session);
      });
    } else if (tutoringSession.domain) {
      startNewTutoringSession(tutoringSession.domain, tutoringSession.initialSkill).then((session) => {
        replaceTopOfStack(session);

        session.interactions.some((interaction) => {
          return interaction.parts.some((interactionPart) => {
            if (interactionPart.isActivity) {
              setActivity({ interaction, interactionPart });
              return true;
            } else {
              return false;
            }
          });
        });
      });
    } else {
      setError(true);
      logError(new Error(`Unable to hydrate tutoring session ${JSON.stringify(tutoringSession)}`));
    }
  };

  const logError = (error: Error, info?: { componentStack: string }) => {
    bugsnagNotify(error);
  };

  const resetTutoringSession = () => {
    replaceTopOfStack({
      domain: tutoringSession.domain,
      initialSkill: tutoringSession.initialSkill
    });
  };

  const shouldCollectRating = () => {
    if (!tutoringSession.interactions) return false;

    const hasQualifyingInteractions = tutoringSession.interactions.some((interaction) => (
      interaction.type === 'assistant' &&
        interaction.status === 'finished' &&
        interaction.parts.some((part) => (
          part.contentProducer?.type === 'response' ||
            part.contentProducer?.type === 'completion' ||
            part.contentProducer?.type === 'action'
        ))
    ));
    if (!hasQualifyingInteractions) return false;

    return askForRating;
  };

  const startNewTutoringSession = (domain: Domain, skill: Skill) => {
    return api.createAITutoringSession({
      currentContext: currentContext(),
      domain: domain.klass,
      initialSkill: skill?.klass
    }).catch((error) => {
      setError(true);
      logError(error);
    });
  };

  const submitUserInput = (text: string) => {
    // Immediately add the user's input to the interaction list for quick UI feedback
    const userInteractionUuid = uuidv4();
    const newUserInteraction = {
      content: {},
      createdAt: moment.utc().valueOf() / 1000,
      finishedAt: moment.utc().valueOf() / 1000,
      id: userInteractionUuid,
      parts: [{
        content: { text },
        contentProducer: null,
        createdAt: moment.utc().valueOf() / 1000,
        finishedAt: moment.utc().valueOf() / 1000,
        id: uuidv4(),
        interactionId: userInteractionUuid,
        isActivity: false,
        name: 'Chat',
        status: 'finished',
        type: 'chat'
      }],
      path: currentContext().path,
      provisional: true,
      status: 'finished',
      tutoringSessionId: tutoringSession.id,
      type: 'user'
    };

    const assistantInteractionUuid = uuidv4();
    const newAssistantInteraction = {
      content: {},
      createdAt: moment.utc().valueOf() / 1000,
      finishedAt: null,
      id: assistantInteractionUuid,
      parts: [{
        content: {},
        contentProducer: null,
        createdAt: moment.utc().valueOf() / 1000,
        finishedAt: null,
        id: uuidv4(),
        interactionId: assistantInteractionUuid,
        isActivity: false,
        name: 'Chat',
        status: 'waiting',
        type: 'chat'
      }],
      path: currentContext().path,
      provisional: true,
      status: 'waiting',
      tutoringSessionId: tutoringSession.id,
      type: 'assistant'
    };
    handleNewInteractions([newUserInteraction, newAssistantInteraction]);

    // When this returns it overwrites what we added above with the real thing
    return api.createAITutoringSessionUserInteraction({
      content: {
        text,
        activeActivityId: activity?.interactionPart?.id
      },
      currentContext: currentContext(),
      tutoringSessionId: tutoringSession.id
    }).then((interactions) => {
      handleNewInteractions(interactions);
    }).catch((error) => {
      bugsnagNotify(error);
      handleNewInteractions([{ ...newAssistantInteraction, status: 'error' }]);
    });
  };

  const tutoringSessionFullyHydrated = () => {
    if (!tutoringSession) return false;
    if (!tutoringSession.id) return false;
    if (!tutoringSession.domain) return false;

    return !!tutoringSession.interactions;
  };

  const waitingOnInteraction = () => {
    return tutoringSession.interactions.some((interaction) => (
      interaction.status !== 'finished' && interaction.status !== 'error'
    ));
  };

  if (!tutoringSession) return null;

  const IconForSkill = iconForSkill(tutoringSession.domain, tutoringSession.initialSkill);

  return (
    <>
      {
        shouldCollectRating() && (
          <div
            style={{
              left: 'calc(50% + 4px)',
              minWidth: 350,
              position: 'absolute',
              top: 24,
              transform: 'translateX(-50%)',
              zIndex: 2
            }}
          >
            <RatingCollection
              dismissible
              onDismiss={() => {
                setAskForRating(false);
                api.updateAITutoringSession({ ...tutoringSession, ratingStatus: 'declined' })
              }}
              rating={{
                ...tutoringSession.rating || {},
                rateableId: tutoringSession.id,
                rateableType: 'TestPrep::AI::TutoringSessions::TutoringSession'
              }}
              rateableName="tutoring session"
            />
          </div>
        )
      }

      <div className="d-flex flex-column" style={{ gap: 15 }}>
        <div className="align-items-end d-flex gap-s">
          <div className="align-items-center d-flex" style={{ gap: 8 }}>
            <div
              className="hidden-xs"
              onClick={tutoringSessionsStack.length > 1 ? popFromStack : clearStack}
              style={{
                background: '#F4F4F4',
                borderRadius: 100,
                color: '#929292',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 'bold',
                padding: '4px 8px'
              }}
            >
              {tutoringSessionsStack.length > 1 ? 'Back' : 'Home'}
            </div>

            <div
              className="visible-xs"
              onClick={tutoringSessionsStack.length > 1 ? popFromStack : clearStack}
              style={{
                color: '#929292',
                cursor: 'pointer',
                padding: '3px 5px'
              }}
            >
              <FontAwesomeIcon icon={tutoringSessionsStack.length > 1 ? 'comment' : 'home'} />
            </div>

            <FontAwesomeIcon icon="chevron-right" style={{ color: '#929292', fontSize: 11 }} />

            <GradientPill size="sm">
              <IconForSkill size={14} /> {tutoringSession.initialSkill?.name || tutoringSession.domain.name}
            </GradientPill>
          </div>

          <div className="align-items-end d-flex flex-1 justify-content-end">
            <div style={{ position: 'relative' }}>
              <Dropdown align="right">
                <Dropdown.Trigger style={{ height: '100%' }}>
                  <a
                    className="align-items-end d-flex justify-self-end text-no-decoration text-hover-no-decoration"
                    style={{ color: '#9AA0A9', height: '100%', padding: '14px 7px 0 7px', }}
                  >
                    <FontAwesomeIcon icon="ellipsis-h" />
                  </a>
                </Dropdown.Trigger>

                <Dropdown.Content>
                  <ul className={style.dropdownItems}>
                    <li>
                      <a onClick={clearStack}>
                        Home
                      </a>
                    </li>

                    <hr className="u-margin-A-n" />

                    <li>
                      <a onClick={resetTutoringSession}>
                        Reset
                      </a>
                    </li>
                  </ul>
                </Dropdown.Content>
              </Dropdown>
            </div>

            <a
              className="align-items-center d-flex small text-bold text-no-decoration"
              onClick={hideTutor}
              style={{ color: '#9AA0A9', height: '100%', padding: 7 }}
            >
              <CrossIcon size={16} />
            </a>
          </div>
        </div>

        <hr className="u-margin-A-n" />
      </div>

      {
        tutoringSessionFullyHydrated() ? (
          <ErrorBoundary fallback={<ErrorMessage />} onError={logError}>
            <div
              className="flex-1"
              id="tutoring-panel-interactions"
              style={{
                opacity: uiStateApplied ? 1 : 0,
                overflowY: 'scroll',
                overscrollBehavior: 'contain'
              }}
            >
              <InteractionsList
                activity={activity}
                addOrUpdateUserInteractions={handleNewInteractions}
                setCurrentActivity={setActivity}
                submitUserInput={submitUserInput}
                tutoringSession={tutoringSession}
              />
            </div>

            <UserInput disabled={waitingOnInteraction()} onSubmit={submitUserInput} />
          </ErrorBoundary>
        ) : (
          error ? (
            <ErrorMessage onRetry={hydrateTutoringSession} />
          ) : (
            <div className="d-flex justify-content-center">
              <LoadingIndicator />
            </div>
          )
        )
      }
    </>
  );
};

export default TutoringPanel;
